import type { TFunction } from "i18next";
import type { TeamPlayer } from "../../types";
import { Check, ChevronDown, ChevronUp, Edit, RotateCcw, X } from "../icons";
import { getPositionBadgeColor } from "./lineupColors";
import {
  toggleTargetZone,
  type ChipDragProps,
  type LineupZone,
} from "./useLineupDnd";
import { formatDisplayName } from "../../lib/nameFormat";
import { formatJerseyNumberDisplay } from "../../lib/players";
import { usePositionReferenceOptions } from "../../hooks/usePositionReferenceOptions";

export interface PlayerChipEditValues {
  jersey_number: number;
  player_name: string;
}

interface PlayerChipProps {
  player: TeamPlayer;
  zone: LineupZone;
  readOnly: boolean;
  toggleDisabled: boolean;
  isDragging: boolean;
  dragProps: ChipDragProps;
  isEditing: boolean;
  editValues: PlayerChipEditValues | null;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onEditJerseyChange: (value: number) => void;
  onEditNameChange: (value: string) => void;
  /** Small remove affordance — only rendered for bench chips (moves the
   * player straight to the "not in squad" zone without cycling through the
   * main toggle). Omit for starters/excluded chips. */
  onRemove?: () => void;
  t: TFunction;
}

export default function PlayerChip({
  player,
  zone,
  readOnly,
  toggleDisabled,
  isDragging,
  dragProps,
  isEditing,
  editValues,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onEditJerseyChange,
  onEditNameChange,
  onRemove,
  t,
}: PlayerChipProps) {
  // Display only — the inline edit field below keeps the complete name.
  const displayName = formatDisplayName(player.player_name) || player.player_id;
  const { labelFor: positionLabelFor } = usePositionReferenceOptions();

  if (isEditing && editValues) {
    return (
      <div
        className="flex items-center justify-between p-2 bg-yellow-50 rounded border border-yellow-300"
        data-testid={`lineup-edit-row-${player.player_id}`}
      >
        <div className="flex items-center space-x-2 flex-1">
          <span className="text-gray-400 text-sm">#</span>
          <input
            type="number"
            min={1}
            max={99}
            value={editValues.jersey_number}
            onChange={(e) =>
              onEditJerseyChange(parseInt(e.target.value, 10) || 0)
            }
            className="w-14 px-1 py-0.5 border rounded text-sm text-center font-semibold"
            data-testid={`lineup-edit-jersey-${player.player_id}`}
          />
          <input
            type="text"
            value={editValues.player_name}
            onChange={(e) => onEditNameChange(e.target.value)}
            className="flex-1 px-2 py-0.5 border rounded text-sm"
            data-testid={`lineup-edit-name-${player.player_id}`}
          />
          <span
            className={`px-2 py-1 rounded text-xs font-medium ${getPositionBadgeColor(
              player.position,
            )}`}
          >
            {positionLabelFor(player.position)}
          </span>
        </div>
        <div className="flex items-center space-x-1 ml-2">
          <button
            type="button"
            onClick={onSaveEdit}
            className="p-1 text-green-600 hover:text-green-800"
            title={t("save")}
            data-testid={`lineup-edit-save-${player.player_id}`}
          >
            <Check className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onCancelEdit}
            className="p-1 text-gray-400 hover:text-gray-600"
            title={t("cancel")}
            data-testid={`lineup-edit-cancel-${player.player_id}`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  const targetZone = toggleTargetZone(zone);
  const toggleLabel =
    targetZone === "starters"
      ? t("lineupBoard.moveToStarters", {
          name: displayName,
          defaultValue: "Move {{name}} to starters",
        })
      : t("lineupBoard.moveToBench", {
          name: displayName,
          defaultValue: "Move {{name}} to bench",
        });
  const isToggleDisabled = readOnly || toggleDisabled;
  const ToggleIcon =
    zone === "starters"
      ? ChevronDown
      : zone === "excluded"
        ? RotateCcw
        : ChevronUp;

  return (
    <div
      className={`flex items-center justify-between p-2 rounded border ${
        isDragging ? "opacity-50" : "border-transparent"
      } ${isToggleDisabled ? "bg-gray-100" : "bg-gray-50"}`}
      data-testid={`lineup-chip-${player.player_id}`}
      data-lineup-zone={zone}
    >
      <button
        type="button"
        disabled={isToggleDisabled}
        aria-label={toggleLabel}
        title={toggleLabel}
        data-testid={`lineup-chip-toggle-${player.player_id}`}
        className="flex items-center space-x-3 flex-1 text-left disabled:cursor-not-allowed disabled:opacity-60"
        draggable={dragProps.draggable && !toggleDisabled}
        onDragStart={dragProps.onDragStart}
        onDragEnd={dragProps.onDragEnd}
        onPointerDown={dragProps.onPointerDown}
        onClick={dragProps.onClick}
      >
        <ToggleIcon className="h-3.5 w-3.5 text-gray-400 shrink-0" />
        <span
          className={`font-semibold ${
            player.jersey_number ? "" : "text-amber-600"
          }`}
          data-testid={
            player.jersey_number
              ? undefined
              : `lineup-chip-missing-jersey-${player.player_id}`
          }
        >
          #{formatJerseyNumberDisplay(player.jersey_number)}
        </span>
        <span className="text-sm text-gray-800">{displayName}</span>
        <span
          className={`px-2 py-1 rounded text-xs font-medium ${getPositionBadgeColor(
            player.position,
          )}`}
        >
          {positionLabelFor(player.position)}
        </span>
      </button>
      {!readOnly && onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="p-1 text-gray-400 hover:text-red-600"
          title={t("lineupBoard.removeFromSquad", {
            name: displayName,
            defaultValue: "Remove {{name}} from the matchday squad",
          })}
          data-testid={`lineup-chip-remove-${player.player_id}`}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
      {!readOnly && (
        <button
          type="button"
          onClick={onStartEdit}
          className="p-1 text-gray-400 hover:text-emerald-600"
          title={t("editLineupPlayer")}
          data-testid={`lineup-edit-btn-${player.player_id}`}
        >
          <Edit className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
